import { z } from 'zod';
import { AccountIdSchema, DateOnlySchema, IssueKeySchema, ProgressSchema, RangeDaysSchema, TimestampSchema, } from './common.js';
import { IssueSummarySchema, UserRefSchema } from './domain.js';
/**
 * Планирование. Полная посуточная сетка с разрывами живёт в БД Jisy: Jira такую структуру
 * не выражает, туда уходят только крайние даты.
 */
export const SyncStateSchema = z.enum(['synced', 'pending', 'conflict', 'error']);
export const PlanDaySchema = z.object({
    day: DateOnlySchema,
    marked: z.boolean(),
});
export const PlanItemSchema = z.object({
    id: z.string(),
    issueKey: IssueKeySchema,
    issue: IssueSummarySchema.nullable(),
    progress: ProgressSchema,
    position: z.number().int().nonnegative(),
    /** Только отмеченные дни; разрывы — это просто отсутствующие даты. */
    days: z.array(DateOnlySchema),
    syncState: SyncStateSchema,
    /** Незакрытые расхождения с Jira по этой строке. */
    conflicts: z.array(z.object({
        id: z.string(),
        field: z.enum(['start_date', 'due_date', 'progress']),
        mine: z.unknown(),
        theirs: z.unknown(),
        detectedAt: TimestampSchema,
    })),
});
export const PlanSchema = z.object({
    id: z.string(),
    owner: UserRefSchema,
    /** Понедельник: неделя всегда начинается с него, независимо от языка интерфейса. */
    startDate: DateOnlySchema,
    rangeDays: RangeDaysSchema,
    items: z.array(PlanItemSchema),
    updatedAt: TimestampSchema,
});
/** Загрузка по дням — считается запросом в БД, а не перебором в приложении. */
export const PlanLoadSchema = z.array(z.object({
    day: DateOnlySchema,
    load: z.number().int().nonnegative(),
}));
/**
 * Сводный ответ режима планирования. При owner=all возвращается несколько планов,
 * загрузка при этом суммируется по всем участникам.
 */
export const PlanViewSchema = z.object({
    startDate: DateOnlySchema,
    rangeDays: RangeDaysSchema,
    plans: z.array(PlanSchema),
    load: PlanLoadSchema,
    /** Может ли текущий пользователь править то, что видит. */
    editable: z.boolean(),
});
/** owner: свой план, план конкретного участника или сводный по всем. */
export const PlanOwnerQuerySchema = z.union([
    z.literal('me'),
    z.literal('all'),
    AccountIdSchema,
]);
export const PlanViewQuerySchema = z.object({
    owner: PlanOwnerQuerySchema.default('me'),
    /** Любая дата внутри недели: сервер сам сдвинет её к понедельнику. */
    start: DateOnlySchema.optional(),
    days: RangeDaysSchema.optional(),
    /** Поиск и фильтры панели планирования. */
    q: z.string().trim().max(200).optional(),
    projectIds: z.array(z.string()).max(50).optional(),
});
export const PlanCreateSchema = z.object({
    ownerAccountId: AccountIdSchema.optional(),
    start: DateOnlySchema,
    days: RangeDaysSchema,
});
/** Пакетное добавление задач из окна подбора. */
export const PlanItemsAddSchema = z.object({
    issueKeys: z.array(IssueKeySchema).min(1).max(200),
});
export const PlanItemPatchSchema = z
    .object({
    progress: ProgressSchema.optional(),
    position: z.number().int().nonnegative().optional(),
})
    .refine((value) => value.progress !== undefined || value.position !== undefined, 'пустая правка');
/**
 * Пакет отметок после протягивания мышью: и проставленные, и снятые дни уходят одним
 * запросом, который на сервере превращается в один INSERT ... ON CONFLICT DO UPDATE.
 */
export const PlanDaysPatchSchema = z.object({
    days: z
        .array(z.object({
        day: DateOnlySchema,
        marked: z.boolean(),
    }))
        .min(1)
        .max(366),
});
export const PlanConflictResolutionSchema = z.object({
    resolution: z.enum(['local', 'remote']),
});
// ── Работа с неделями ────────────────────────────────────────────────────────
/** Понедельник недели, в которую попадает дата. Считается в UTC, без часовых поясов. */
export function mondayOf(date) {
    const parsed = new Date(`${date}T00:00:00Z`);
    const weekday = parsed.getUTCDay(); // 0 — воскресенье
    const shift = weekday === 0 ? -6 : 1 - weekday;
    parsed.setUTCDate(parsed.getUTCDate() + shift);
    return toDateOnly(parsed);
}
/** Список дат диапазона плана, начиная с понедельника. */
export function planDays(start, days) {
    const base = new Date(`${start}T00:00:00Z`);
    const out = [];
    for (let i = 0; i < days; i += 1) {
        const day = new Date(base);
        day.setUTCDate(base.getUTCDate() + i);
        out.push(toDateOnly(day));
    }
    return out;
}
/** Суббота и воскресенье: в сетке они обычные рабочие колонки, песочная у них только шапка. */
export function isWeekend(date) {
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    return weekday === 0 || weekday === 6;
}
export function addDays(date, amount) {
    const parsed = new Date(`${date}T00:00:00Z`);
    parsed.setUTCDate(parsed.getUTCDate() + amount);
    return toDateOnly(parsed);
}
export function toDateOnly(date) {
    const iso = date.toISOString();
    return iso.slice(0, 10);
}
/**
 * Отмеченные дни, свёрнутые в непрерывные отрезки. Разрывы сохраняются: пн–вт, пропуск,
 * чт–пт даёт два отрезка, а не один длинный. На этом построена отрисовка сетки и подпись длины.
 */
export function toSegments(days) {
    if (days.length === 0)
        return [];
    const sorted = [...days].sort();
    const segments = [];
    let start = sorted[0];
    let previous = start;
    let length = 1;
    for (let i = 1; i < sorted.length; i += 1) {
        const current = sorted[i];
        if (current === addDays(previous, 1)) {
            previous = current;
            length += 1;
            continue;
        }
        segments.push({ start, end: previous, length });
        start = current;
        previous = current;
        length = 1;
    }
    segments.push({ start, end: previous, length });
    return segments;
}
/** Крайние даты плана — ровно то, что уходит в Jira. */
export function planBounds(days) {
    if (days.length === 0)
        return null;
    const sorted = [...days].sort();
    return {
        start: sorted[0],
        end: sorted[sorted.length - 1],
    };
}
//# sourceMappingURL=plan.js.map